import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, XCircle, CheckCircle2 } from "lucide-react";

/**
 * Take / Skip Trade decision UI (Stage B).
 * "Take" is intent-only — the actual approval still happens via /approve.
 * "Skip" captures a structured reason for future House Playbook analytics.
 */
const SKIP_REASONS = [
  { code: "risk_too_high", label: "Risk too high" },
  { code: "waiting_for_pullback", label: "Waiting for pullback" },
  { code: "capital_unavailable", label: "Capital unavailable" },
  { code: "already_have_similar_exposure", label: "Already have similar exposure" },
  { code: "better_opportunity_elsewhere", label: "Better opportunity elsewhere" },
  { code: "did_not_agree_with_ai", label: "Did not agree with AI" },
  { code: "other", label: "Other" },
];

export default function TakeSkipTrade({ proposalId, decision, onDecided, onOpenPrepare }) {
  const [skipOpen, setSkipOpen] = useState(false);
  const [reasonCode, setReasonCode] = useState("");
  const [reasonText, setReasonText] = useState("");
  const [busy, setBusy] = useState(false);

  const isTaken = decision?.action === "take";
  const isSkipped = decision?.action === "skip";

  const markTake = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/proposals/${proposalId}/take`);
      toast.success("Intent recorded — opening trade placement.");
      onDecided?.(data.decision);
      // Immediately open the Prepare Trade dialog so the user can actually
      // place the order. Recording intent alone left users stranded on the
      // Proposal page with no visible next step.
      onOpenPrepare?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const submitSkip = async () => {
    if (!reasonCode) return toast.error("Please select a reason");
    setBusy(true);
    try {
      const { data } = await api.post(`/proposals/${proposalId}/skip`, {
        reason_code: reasonCode, reason_text: reasonText,
      });
      toast.success("Skip recorded. Thanks — this feedback improves future recommendations.");
      onDecided?.(data.decision);
      setSkipOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (isTaken) {
    return (
      <div className="border border-signal-green/40 bg-signal-green/5 px-3 py-2 flex items-center gap-2" data-testid="decision-taken-banner">
        <CheckCircle2 size={14} className="pnl-up" />
        <span className="mono uppercase text-[11px] tracking-widest pnl-up">Marked to Take</span>
        <span className="mono text-[10px] text-muted-foreground ml-auto">{new Date(decision.decided_at).toLocaleString()}</span>
        {onOpenPrepare && (
          <button className="btn-primary h-7 !px-2 text-[11px]" onClick={onOpenPrepare} data-testid="place-trade-btn">
            Place Trade
          </button>
        )}
      </div>
    );
  }
  if (isSkipped) {
    const reasonLabel = SKIP_REASONS.find(r => r.code === decision.reason_code)?.label || decision.reason_code || "Skipped";
    return (
      <div className="border border-signal-yellow/40 bg-signal-yellow/5 px-3 py-2 space-y-1" data-testid="decision-skipped-banner">
        <div className="flex items-center gap-2">
          <XCircle size={14} className="text-signal-yellow" />
          <span className="mono uppercase text-[11px] tracking-widest text-signal-yellow">Skipped · {reasonLabel}</span>
          <span className="mono text-[10px] text-muted-foreground ml-auto">{new Date(decision.decided_at).toLocaleString()}</span>
        </div>
        {decision.reason_text && <div className="text-[11px] text-muted-foreground italic">"{decision.reason_text}"</div>}
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-2 gap-2" data-testid="take-skip-controls">
        <button className="btn-primary" disabled={busy} onClick={markTake} data-testid="take-trade-btn">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
          Take Trade
        </button>
        <button className="btn-outline" disabled={busy} onClick={() => setSkipOpen(true)} data-testid="skip-trade-btn">
          <XCircle size={13} /> Skip Trade
        </button>
      </div>

      <Dialog open={skipOpen} onOpenChange={(v) => !v && setSkipOpen(false)}>
        <DialogContent className="!rounded-none border-border/70 max-w-md" data-testid="skip-trade-dialog">
          <DialogHeader>
            <DialogTitle className="mono uppercase tracking-wider text-sm">Skip Trade — why?</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <div className="text-xs text-muted-foreground">This feedback improves future recommendations. Your reason is stored with the analysis for the House Playbook.</div>
            <div className="space-y-1">
              {SKIP_REASONS.map((r) => (
                <label key={r.code} className={`flex items-center gap-2 border p-2 cursor-pointer text-sm ${reasonCode === r.code ? "border-[#0A84FF]/60 bg-[#0A84FF]/5" : "border-border/60 hover:bg-secondary/20"}`}>
                  <input type="radio" name="skip-reason" value={r.code} checked={reasonCode === r.code} onChange={() => setReasonCode(r.code)} data-testid={`skip-reason-${r.code}`}/>
                  {r.label}
                </label>
              ))}
            </div>
            {reasonCode === "other" && (
              <Input placeholder="Describe why…" value={reasonText} onChange={(e) => setReasonText(e.target.value)}
                className="!rounded-none mono" data-testid="skip-reason-text"/>
            )}
          </div>
          <DialogFooter>
            <button className="btn-outline" onClick={() => setSkipOpen(false)}>Cancel</button>
            <button className="btn-primary" disabled={!reasonCode || busy} onClick={submitSkip} data-testid="skip-submit-btn">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <XCircle size={13} />}
              Record Skip
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
